import React, { useEffect, useRef, useState, useMemo } from 'react';
import { MapPin, AlertTriangle, Loader2, Wifi, Shield, TrendingUp, Flag, Server, Eye } from 'lucide-react';
import { useGetMapDataQuery } from '@/lib/services/searchApi';
import type { MapDataLocation, SeverityType } from '@/lib/api/types';

interface AdvancedThreatMapProps {
    fetchId: string | null;
    className?: string;
    onLocationSelect?: (location: any) => void;
    data?: any;
    isLoading?: boolean;
}

interface EnhancedMapLocation extends MapDataLocation {
    threatCount: number;
    severity: SeverityType;
    dataType: string;
    lastSeen: string;
}

interface CountryStats {
    country: string;
    countryCode: string;
    count: number;
    percentage: number;
    cities: Set<string>;
    regions: Set<string>;
    ips: string[];
}

let L: any = null;

const SearchThreatMap: React.FC<AdvancedThreatMapProps> = ({
    fetchId,
    className = '',
    onLocationSelect,
    isLoading
}) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<any>(null);
    const markersRef = useRef<any[]>([]);

    // FIX: Using a ref to prevent onLocationSelect from triggering map rebuilds
    const onLocationSelectRef = useRef(onLocationSelect);
    useEffect(() => {
        onLocationSelectRef.current = onLocationSelect;
    }, [onLocationSelect]);

    const [mapLoaded, setMapLoaded] = useState<boolean>(false);
    const [selectedView, setSelectedView] = useState<'country' | 'individual'>('country');
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);

    const { data: mapData, isLoading: isFetchingData, error } = useGetMapDataQuery(fetchId!, {
        skip: !fetchId,
        pollingInterval: 0,
    });

    const enhancedLocations = useMemo(() => {
        if (!mapData?.data?.locations) return [];
        return mapData.data.locations.map((location: any): EnhancedMapLocation => ({
            ...location,
            threatCount: 1,
            severity: 'medium' as SeverityType,
            dataType: 'ip',
            lastSeen: new Date().toISOString(),
        }));
    }, [mapData]);

    const countryStats = useMemo(() => {
        if (!mapData?.data) return [];
        const { locations, totalIPs } = mapData.data;
        const statsMap = new Map<string, CountryStats>();

        locations.forEach((location: any) => {
            const { country, countryCode, city, region, ip } = location;
            const safeCountry = country || 'Unknown';
            if (!statsMap.has(safeCountry)) {
                statsMap.set(safeCountry, {
                    country: safeCountry, countryCode: countryCode || 'UN', count: 0, percentage: 0,
                    cities: new Set<string>(), regions: new Set<string>(), ips: []
                });
            }
            const stats = statsMap.get(safeCountry)!;
            stats.count++;
            if (city) stats.cities.add(city);
            if (region) stats.regions.add(region);
            stats.ips.push(ip);
        });

        const statsArray = Array.from(statsMap.values()).map((stats: CountryStats) => ({
            ...stats,
            percentage: totalIPs > 0 ? (stats.count / totalIPs) * 100 : 0
        }));

        return statsArray.sort((a, b) => b.count - a.count);
    }, [mapData]);

    const mapStats = useMemo(() => {
        if (!mapData?.data) return null;
        const { locations, totalIPs, publicIPs, geolocatedIPs, countryCounts } = mapData.data;
        return {
            totalLocations: locations?.length || 0,
            uniqueCountries: Object.keys(countryCounts || {}).length,
            totalIPs: totalIPs || 0,
            publicIPs: publicIPs || 0,
            geolocatedIPs: geolocatedIPs || 0,
            geolocatedPercentage: totalIPs > 0 ? Math.round((geolocatedIPs / totalIPs) * 100) : 0,
            privateIPs: Math.max(0, (totalIPs || 0) - (publicIPs || 0)),
            privatePercentage: totalIPs > 0 ? Math.round(((totalIPs - publicIPs) / totalIPs) * 100) : 0,
        };
    }, [mapData]);

    const getCountryColor = (count: number, totalCount: number): string => {
        if (totalCount === 0) return '#50A970';
        const percentage = (count / totalCount) * 100;
        if (percentage >= 20) return '#FF3333';
        if (percentage >= 10) return '#ea580c';
        if (percentage >= 5) return '#d97706';
        if (percentage >= 2) return '#00FFFF';
        return '#50A970';
    };

    const getCountryRadius = (count: number): number => {
        if (count >= 20) return 26;
        if (count >= 10) return 22;
        if (count >= 5) return 18;
        return 14;
    };

    useEffect(() => {
        const initMap = async () => {
            if (!mapRef.current) return;

            try {
                if (!L) {
                    const leafletModule = await import('leaflet');
                    L = leafletModule.default;
                }

                if (!document.querySelector('link[href*="leaflet.css"]')) {
                    const link = document.createElement('link');
                    link.rel = 'stylesheet';
                    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
                    document.head.appendChild(link);
                }

                if (mapInstanceRef.current) {
                    markersRef.current.forEach((marker: any) => mapInstanceRef.current.removeLayer(marker));
                    markersRef.current = [];
                    mapInstanceRef.current.remove();
                    mapInstanceRef.current = null;
                }

                mapInstanceRef.current = L.map(mapRef.current, {
                    center: [20, 0],
                    zoom: 2,
                    zoomControl: false,
                    attributionControl: true, // Updated to comply with CARTO terms
                    minZoom: 1,
                    maxZoom: 18
                });

                const cartoApiKey = import.meta.env.VITE_CARTO_API_KEY || 'YOUR_API_KEY_HERE';

                L.tileLayer(`https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=${cartoApiKey}`, {
                    subdomains: 'abcd',
                    maxZoom: 19,
                    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                }).addTo(mapInstanceRef.current);

                if (enhancedLocations.length > 0) {
                    if (selectedView === 'country') {
                        const countryLocationMap = new Map<string, EnhancedMapLocation[]>();

                        enhancedLocations.forEach((location: EnhancedMapLocation) => {
                            const country = location.country || 'Unknown';
                            if (!countryLocationMap.has(country)) countryLocationMap.set(country, []);
                            countryLocationMap.get(country)!.push(location);
                        });

                        countryLocationMap.forEach((locations: EnhancedMapLocation[], country: string) => {
                            const countryCount = locations.length;
                            const primaryLocation = locations[0];
                            const countryData = countryStats.find((cs: CountryStats) => cs.country === country);

                            const radius = getCountryRadius(countryCount);
                            const color = getCountryColor(countryCount, mapStats?.totalIPs || 1);
                            const safePercentage = (countryData?.percentage || 0).toFixed(1);

                            const countryIcon = L.divIcon({
                                className: 'country-threat-marker',
                                html: `
                                    <div style="width: ${radius * 2}px; height: ${radius * 2}px; position: relative;">
                                        <div style="position: absolute; width: 100%; height: 100%; border: 2px solid ${color}; border-radius: 50%; background: ${color}20; box-shadow: 0 0 15px ${color}50; animation: country-pulse 3s infinite;"></div>
                                        <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: white; font-weight: bold; font-family: monospace; font-size: ${Math.max(9, radius / 2.5)}px; text-shadow: 0 0 4px #000;">${primaryLocation.countryCode || 'UN'}</div>
                                        <div style="position: absolute; top: -5px; right: -5px; background: ${color}; color: black; border-radius: 4px; padding: 1px 4px; font-size: 9px; font-weight: bold; font-family: monospace;">${countryCount}</div>
                                    </div>
                                `,
                                iconSize: [radius * 2, radius * 2],
                                iconAnchor: [radius, radius]
                            });

                            const marker = L.marker([primaryLocation.lat, primaryLocation.lng], { icon: countryIcon });
                            const uniqueCities = [...new Set(locations.map((l: EnhancedMapLocation) => l.city).filter(Boolean))];

                            const popupContent = `
                                <div style="color: #94a3b8; font-family: monospace; min-width: 280px; background: #0A0B0E; padding: 0;">
                                    <div style="background: linear-gradient(135deg, #12151C, #0A0B0E); border-bottom: 1px solid #1A1D24; padding: 16px;">
                                        <div style="display: flex; align-items: center; gap: 12px;">
                                            <div style="width: 32px; height: 32px; background: #1A1D24; border: 1px solid ${color}; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 12px; color: ${color};">
                                                ${primaryLocation.countryCode || 'UN'}
                                            </div>
                                            <div>
                                                <div style="font-weight: bold; font-size: 14px; color: white;">${country.toUpperCase()}</div>
                                                <div style="font-size: 10px; color: #50A970;">${safePercentage}% OF THREATS</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div style="padding: 16px;">
                                        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 16px;">
                                            <div style="text-align: center; padding: 8px; background: #12151C; border: 1px solid #1A1D24; border-radius: 6px;">
                                                <div style="font-size: 16px; font-weight: bold; color: ${color};">${countryCount}</div>
                                                <div style="font-size: 9px; letter-spacing: 1px; color: #64748b;">HOSTS</div>
                                            </div>
                                            <div style="text-align: center; padding: 8px; background: #12151C; border: 1px solid #1A1D24; border-radius: 6px;">
                                                <div style="font-size: 16px; font-weight: bold; color: #00FFFF;">${uniqueCities.length}</div>
                                                <div style="font-size: 9px; letter-spacing: 1px; color: #64748b;">CITIES</div>
                                            </div>
                                        </div>
                                        <div style="margin-bottom: 12px;">
                                            <div style="font-size: 10px; letter-spacing: 1px; color: #64748b; margin-bottom: 6px;">TOP CITIES:</div>
                                            <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                                                ${uniqueCities.slice(0, 4).map(city => `<span style="background: #12151C; border: 1px solid #1A1D24; color: #94a3b8; padding: 2px 6px; border-radius: 4px; font-size: 9px;">${city}</span>`).join('')}
                                                ${uniqueCities.length > 4 ? `<span style="color: #64748b; font-size: 9px; padding: 2px;">+${uniqueCities.length - 4} MORE</span>` : ''}
                                            </div>
                                        </div>
                                        <div>
                                            <div style="font-size: 10px; letter-spacing: 1px; color: #64748b; margin-bottom: 6px;">SAMPLE IPS:</div>
                                            <div style="background: #12151C; border: 1px solid #1A1D24; padding: 8px; border-radius: 6px; font-size: 10px; color: #50A970; line-height: 1.6;">
                                                ${locations.slice(0, 3).map(l => l.ip).join('<br>')}
                                                ${locations.length > 3 ? `<span style="color: #64748b;">...AND ${locations.length - 3} MORE</span>` : ''}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            `;

                            marker.bindPopup(popupContent, { maxWidth: 360, className: 'cyber-popup' });
                            marker.on('click', () => {
                                setSelectedCountry(prev => prev === country ? null : country);
                                if (onLocationSelectRef.current && countryData) onLocationSelectRef.current(countryData as any);
                            });

                            mapInstanceRef.current.addLayer(marker);
                            markersRef.current.push(marker);
                        });

                    } else {
                        enhancedLocations.forEach((location: EnhancedMapLocation) => {
                            const color = '#00FFFF';
                            const radius = 6;

                            const ipIcon = L.divIcon({
                                className: 'ip-threat-marker',
                                html: `<div style="width: ${radius * 2}px; height: ${radius * 2}px; position: relative;"><div style="width: 100%; height: 100%; background: ${color}; border-radius: 50%; box-shadow: 0 0 10px ${color}80; animation: ip-pulse 2s infinite;"></div></div>`,
                                iconSize: [radius * 2, radius * 2],
                                iconAnchor: [radius, radius]
                            });

                            const marker = L.marker([location.lat, location.lng], { icon: ipIcon });
                            const popupContent = `
                                <div style="background: #0A0B0E; border: 1px solid #1A1D24; border-radius: 8px; padding: 12px; font-family: monospace; min-width: 200px;">
                                    <div style="color: #00FFFF; font-weight: bold; font-size: 14px; margin-bottom: 8px; border-bottom: 1px solid #1A1D24; padding-bottom: 4px;">${location.ip}</div>
                                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 10px;">
                                        <div><div style="color: #64748b; margin-bottom: 2px;">COUNTRY</div><div style="color: white;">${location.countryCode || 'N/A'}</div></div>
                                        <div><div style="color: #64748b; margin-bottom: 2px;">REGION</div><div style="color: white;">${location.region || 'N/A'}</div></div>
                                    </div>
                                </div>
                            `;

                            marker.bindPopup(popupContent, { className: 'cyber-popup' });
                            marker.on('click', () => { if (onLocationSelectRef.current) onLocationSelectRef.current({ ...location, ips: [location.ip] } as any); });
                            mapInstanceRef.current.addLayer(marker);
                            markersRef.current.push(marker);
                        });
                    }
                }

                // FIX: Added animate: false to completely resolve the _leaflet_pos crash bug
                if (markersRef.current.length > 0) {
                    const group = new L.featureGroup(markersRef.current);
                    mapInstanceRef.current.fitBounds(group.getBounds(), { padding: [20, 20], maxZoom: selectedView === 'country' ? 6 : 10, animate: false });
                } else {
                    mapInstanceRef.current.setView([20, 0], 2, { animate: false });
                }

                if (!document.querySelector('#cyber-map-styles')) {
                    const style = document.createElement('style');
                    style.id = 'cyber-map-styles';
                    style.textContent = `
                        @keyframes country-pulse { 0% { transform: scale(0.9); opacity: 1; } 100% { transform: scale(1.3); opacity: 0; } }
                        @keyframes ip-pulse { 0% { box-shadow: 0 0 5px #00FFFF80; } 50% { box-shadow: 0 0 15px #00FFFF, 0 0 5px #00FFFF80; } 100% { box-shadow: 0 0 5px #00FFFF80; } }
                        .cyber-popup .leaflet-popup-content-wrapper { background: #0A0B0E; border: 1px solid #50A970; border-radius: 8px; padding: 0; box-shadow: 0 0 20px rgba(0,0,0,0.8); overflow: hidden; }
                        .cyber-popup .leaflet-popup-tip { background: #0A0B0E; border: 1px solid #50A970; }
                        .cyber-popup .leaflet-popup-content { margin: 0; }
                        .leaflet-container { background: #000000 !important; }
                    `;
                    document.head.appendChild(style);
                }

                setMapLoaded(true);
            } catch (error) { console.error('Error initializing Leaflet map:', error); }
        };

        initMap();

        return () => {
            if (mapInstanceRef.current) {
                // FIX: Forcefully stop all active Leaflet animations before destroying the container
                if (mapInstanceRef.current?.stop) mapInstanceRef.current.stop();

                markersRef.current.forEach((marker: any) => mapInstanceRef.current.removeLayer(marker));
                markersRef.current = [];
                mapInstanceRef.current.off();
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, [enhancedLocations, selectedView]);

    if ((isFetchingData || isLoading) && !mapData) {
        return (
            <div className={`w-full h-[400px] flex flex-col items-center justify-center bg-[#0A0B0E] border border-[#1A1D24] rounded-lg ${className}`}>
                <Loader2 className="h-8 w-8 text-[#50A970] animate-spin mb-4" />
                <p className="text-[12px] text-slate-400 font-mono tracking-widest uppercase">Fetching Geographic Nodes...</p>
            </div>
        );
    }

    if (error) {
        const isGone = (error as any)?.status === 410;
        return (
            <div className={`w-full h-[400px] flex flex-col items-center justify-center bg-[#0A0B0E] border border-[#FF3333]/30 rounded-lg ${className}`}>
                <AlertTriangle className="h-8 w-8 text-[#FF3333] mb-4" />
                <p className="text-[12px] text-slate-400 font-mono tracking-widest uppercase mb-2">
                    {isGone ? "Map Session Expired" : "Topology Sync Failed"}
                </p>
                <p className="text-[10px] text-slate-500 font-mono max-w-xs text-center mb-4">
                    {isGone ? "Geographic data cache purged. Please re-run search." : "Unable to fetch geographic data."}
                </p>
            </div>
        );
    }

    if (!fetchId) return null;

    return (
        <div className={`space-y-4 ${className} font-mono`}>
            {mapStats && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                    <div className="bg-[#12151C] border border-[#1A1D24] rounded-lg p-3">
                        <div className="text-[10px] text-slate-500 tracking-widest mb-1 flex items-center gap-1"><Server className="w-3 h-3 text-[#50A970]" /> TOTAL IPs</div>
                        <div className="text-lg font-bold text-white">{mapStats.totalIPs.toLocaleString()}</div>
                    </div>
                    <div className="bg-[#12151C] border border-[#1A1D24] rounded-lg p-3">
                        <div className="text-[10px] text-slate-500 tracking-widest mb-1 flex items-center gap-1"><Wifi className="w-3 h-3 text-[#00FFFF]" /> PUBLIC</div>
                        <div className="text-lg font-bold text-white">{mapStats.publicIPs.toLocaleString()}</div>
                    </div>
                    <div className="bg-[#12151C] border border-[#1A1D24] rounded-lg p-3">
                        <div className="text-[10px] text-slate-500 tracking-widest mb-1 flex items-center gap-1"><Shield className="w-3 h-3 text-orange-500" /> PRIVATE</div>
                        <div className="text-lg font-bold text-white">{mapStats.privateIPs.toLocaleString()}</div>
                    </div>
                    <div className="bg-[#12151C] border border-[#1A1D24] rounded-lg p-3">
                        <div className="text-[10px] text-slate-500 tracking-widest mb-1 flex items-center gap-1"><MapPin className="w-3 h-3 text-purple-500" /> MAPPED</div>
                        <div className="text-lg font-bold text-[#50A970]">{mapStats.geolocatedPercentage}%</div>
                    </div>
                    <div className="bg-[#12151C] border border-[#1A1D24] rounded-lg p-3">
                        <div className="text-[10px] text-slate-500 tracking-widest mb-1 flex items-center gap-1"><Flag className="w-3 h-3 text-[#FF3333]" /> REGIONS</div>
                        <div className="text-lg font-bold text-white">{mapStats.uniqueCountries}</div>
                    </div>
                    <div className="bg-[#12151C] border border-[#1A1D24] rounded-lg p-3">
                        <div className="text-[10px] text-slate-500 tracking-widest mb-1 flex items-center gap-1"><Eye className="w-3 h-3 text-[#00FFFF]" /> DEVICES</div>
                        <div className="text-lg font-bold text-white">{mapStats.totalLocations}</div>
                    </div>
                </div>
            )}

            <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1 relative bg-black border border-[#1A1D24] rounded-lg overflow-hidden h-[450px]">
                    <div className="absolute top-4 left-4 z-[400] flex gap-2">
                        <button
                            onClick={() => setSelectedView('country')}
                            className={`px-3 py-1.5 text-[10px] font-bold rounded flex items-center gap-2 transition-all ${selectedView === 'country' ? 'bg-[#50A970] text-black shadow-[0_0_10px_rgba(80,169,112,0.3)]' : 'bg-[#12151C] border border-[#1A1D24] text-slate-400 hover:text-white'}`}
                        >
                            <Flag className="w-3 h-3" /> AGGREGATE
                        </button>
                        <button
                            onClick={() => setSelectedView('individual')}
                            className={`px-3 py-1.5 text-[10px] font-bold rounded flex items-center gap-2 transition-all ${selectedView === 'individual' ? 'bg-[#00FFFF] text-black shadow-[0_0_10px_rgba(0,255,255,0.3)]' : 'bg-[#12151C] border border-[#1A1D24] text-slate-400 hover:text-white'}`}
                        >
                            <Server className="w-3 h-3" /> RAW NODES
                        </button>
                    </div>

                    <div ref={mapRef} className="w-full h-full bg-black z-0" />

                    {!mapLoaded && (
                        <div className="absolute inset-0 z-50 bg-[#0A0B0E]/80 backdrop-blur-sm flex flex-col items-center justify-center">
                            <Loader2 className="h-8 w-8 text-[#50A970] animate-spin mb-3" />
                            <p className="text-[10px] tracking-widest text-slate-400 uppercase">Mounting Leaflet Engine...</p>
                        </div>
                    )}
                </div>

                {selectedView === 'country' && (
                    <div className="w-full lg:w-80 bg-[#0A0B0E] border border-[#1A1D24] rounded-lg p-4 flex flex-col h-[450px]">
                        <div className="flex items-center gap-2 mb-4 pb-4 border-b border-[#1A1D24]">
                            <TrendingUp className="h-4 w-4 text-[#50A970]" />
                            <h4 className="text-[12px] font-bold text-white tracking-widest uppercase">Top Origin Vectors</h4>
                        </div>

                        {countryStats.length > 0 ? (
                            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-2 pr-2">
                                {countryStats.slice(0, 15).map((country: CountryStats, index: number) => (
                                    <div
                                        key={country.country}
                                        className={`p-3 rounded-lg border transition-colors cursor-pointer ${selectedCountry === country.country
                                            ? 'bg-[#12151C] border-[#50A970]/50 shadow-[0_0_10px_rgba(80,169,112,0.1)]'
                                            : 'bg-transparent border-[#1A1D24] hover:bg-[#12151C]'
                                            }`}
                                        onClick={() => {
                                            setSelectedCountry(prev => prev === country.country ? null : country.country);
                                            if (onLocationSelect) onLocationSelect(country as any);
                                        }}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center justify-center w-5 h-5 bg-[#1A1D24] rounded text-[10px] font-bold text-slate-400">
                                                    {index + 1}
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-[11px] font-bold text-white truncate max-w-[100px]">{country.country}</span>
                                                    <span className="text-[9px] text-slate-500">{country.countryCode}</span>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-[13px] font-bold text-white">{country.count}</div>
                                                <div className="text-[9px] text-[#50A970]">{country.percentage.toFixed(1)}%</div>
                                            </div>
                                        </div>
                                        <div className="w-full bg-[#1A1D24] rounded-full h-[3px]">
                                            <div className="h-[3px] rounded-full" style={{ width: `${country.percentage}%`, backgroundColor: getCountryColor(country.count, mapStats?.totalIPs || 1) }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full text-slate-500">
                                <MapPin className="w-8 h-8 mb-3 opacity-30" />
                                <span className="text-[10px] font-mono tracking-widest text-center">NO GEOSPATIAL DATA<br />AVAILABLE FOR THIS QUERY</span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchThreatMap;